import { useState, useEffect } from "react";
import { FilterMenu } from "../filter-menu/FilterMenu";
import KeywordSearch from "../keyword-search/KeywordSearch";
import { useTheme } from "@kaltura/mediaspace-shared-styled";
import { useMediaQuery } from "@kaltura/mediaspace-shared-utils";
import { ButtonClickAnalyticsType } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import type { SearchBarProps } from "../SearchBar";
import { StyledSearchBar, StyledKeywordSearchWrapper } from "../SearchBar.styles";
import { SearchBarButtons } from "../search-bar-buttons/SearchBarButtons";

/**
 * Search bar without autocomplete suggestions.
 */
export function SearchBarBase(props: SearchBarProps) {
    const { filterMenuProps, keywordSearchProps, buttons, leftButtons, fullWidth } = props;
    const keywordPropValue = keywordSearchProps.value || "";
    const [keyword, setKeyword] = useState(keywordPropValue);

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.sm));
    const showFullWidth = fullWidth || (isSmallScreen && !!buttons && !!leftButtons);
    const sendButtonAnalytics = useButtonAnalytics();

    // Reset internal keyword state on external changes (e.g. when clicking "clear all filters")
    useEffect(() => {
        setKeyword(keywordPropValue);
    }, [keywordPropValue]);

    const handleSubmit = (value: string) => {
        keywordSearchProps.onSubmit(value);
        setKeyword(value);
        sendButtonAnalytics("Submit Search", ButtonClickAnalyticsType.SEARCH);
    };

    const handleKeywordChange = (value: string) => {
        setKeyword(value);
    };

    // show menu iff there is a keyword and menu data:
    const menu = keywordSearchProps.value && filterMenuProps ? <FilterMenu {...filterMenuProps} /> : undefined;

    return (
        <StyledSearchBar
            fullWidth={fullWidth}
            switchToVerticalOnMobile={!!buttons && !!leftButtons}
            className="kms-ds-searchbar"
        >
            <StyledKeywordSearchWrapper fullWidth={showFullWidth}>
                <KeywordSearch
                    {...keywordSearchProps}
                    value={keyword}
                    endAdornment={menu}
                    onSubmit={handleSubmit}
                    onChange={handleKeywordChange}
                    fullWidth={showFullWidth}
                />
            </StyledKeywordSearchWrapper>
            <SearchBarButtons buttons={buttons} leftButtons={leftButtons} />
        </StyledSearchBar>
    );
}
